import Section from '../../components-library/checkout/sections/Section';
import useBasket from '../../hooks/basket/useBasket';
import classnames from 'classnames';
import useScreenType from '../../hooks/utility/useScreenType';
import useRouter from '../../hooks/navigation/useRouter';
import BackIcon from '../../assets/logo/BackIcon';
import useTheme from '../../hooks/ui/useTheme';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import useDraftError from '../../hooks/order/useDraftError';
import PayButton from '../../components-library/payment/buttons/PayButton';
import EmptyBasketIllustration from '../../assets/illustrations/EmptyBasketIllustration';
import PaymentCoordinator from '../../components-library/payment/PaymentCoordinator';
import LoadingPage from '../../components-library/loading/LoadingPage';
import OrderDraftErrorPage from '../../components-library/checkout/OrderDraftErrorPage';
import { useTranslation } from 'react-i18next';
import OrderPaymentBreakdown from '../../components-library/order/OrderPaymentBreakdown';
import DeliveryThreshold from '../../components-library/delivery/DeliveryThreshold';
import { SCREENS } from '../../services/exports/Constants';
import CheckoutThreshold from '../../components-library/checkout/CheckoutThreshold';
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import FixedPageLayout from '../../components-library/layouts/FixedPageLayout';
import CheckoutButton from '../../components-library/buttons/CheckoutButton';
import PaymentInfoSection from '../../components-library/checkout/sections/PaymentInfoSection';
import ExpectedCashback from '../../components-library/cashback/ExpectedCashback';
import { Routes } from '../../modules/mobile/exports/Enums';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import BasketItemModifiers from '../../components-library/basket/BasketItemModifiers';
import PriceComparison from '../../components-library/menu/PriceComparison';
import OfferLineBreakdown from '../../features/offers/components/OfferLineBreakdown';
import OrderLineBadges from '../../components-library/order/OrderLineBadges';
import useRefreshSavedPaymentOptions from '../../hooks/payment/useRefreshSavedPaymentOptions';

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return <>{children}</>;
  }

  return <PaymentCoordinator>{children}</PaymentCoordinator>;
};

function OrderPaymentScreen({ className }: { className?: string }) {
  const { data, setIgnorePaymentMethod } = useOrderFlow();
  const { isDesktop } = useScreenType();

  // No `recoversDeliveryDetails`: this screen has no address modal, so an incomplete
  // address is as terminal here as any other failure — it just needs its own copy.
  const { show: showDraftError, isDeliveryDetails } = useDraftError();

  useRefreshSavedPaymentOptions();

  // Ionic's IonNav keeps the previous screen mounted on forward navigation, so the
  // OrderConfirmationScreen unmount cleanup that resets ignorePaymentMethod never fires.
  // Reset it explicitly when the payment screen mounts so the payment flow can submit
  // the selected method instead of `null`.
  useEffect(() => {
    setIgnorePaymentMethod(false);
  }, []);

  // Returned outside <WithWrapper> on purpose: PaymentCoordinator creates a Stripe
  // PaymentIntent and mounts the Adyen drop-in on mount, and there is no order to pay for.
  // The Ionic pages keep it mounted at page level, where it wraps the footer too.
  if (showDraftError) {
    return <OrderDraftErrorPage isDeliveryDetails={isDeliveryDetails} />;
  }

  return (
    <WithWrapper>
      <div className="min-h-screen bg-gray-background">
        {!data.id && <LoadingPage />}
        {isDesktop ? <Desktop /> : <Mobile className={className} />}
      </div>
    </WithWrapper>
  );
}

const Desktop = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderPaymentScreen' });

  const { items } = useBasket();
  const { data, loading } = useOrderFlow();

  return (
    <FixedPageLayout
      hasHeader={false}
      leftColumn={
        <OrderPaymentScreen.LeftColumn>
          <PaymentInfoSection show showTerms />
        </OrderPaymentScreen.LeftColumn>
      }
      rightColumn={
        <>
          {items.length > 0 ? (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto p-md space-y-md scrollbar-hide">
                <h2 className="text-base font-semibold text-brand-text-primary">{t('sections.order_summary')}</h2>
                <DeliveryThreshold />
                <div className="space-y-sm">
                  {items.map((item, index) => (
                    <div key={`basket-item-${index}`} className="space-y-2xs">
                      <div className="text-sm flex justify-between gap-md">
                        <span className="text-muted-foreground">
                          {item.quantity}x {item.entry?.name}
                        </span>
                        <PriceComparison
                          className="shrink-0"
                          originalPrice={item.original_price}
                          actualPrice={item.actual_price}
                        />
                      </div>
                      <OfferLineBreakdown item={item} />
                      <OrderLineBadges badges={item.badges} />
                      <BasketItemModifiers item={item} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-md">
                <hr />
              </div>
              <div className="border-brand-border p-md space-y-md">
                <OrderPaymentBreakdown order={data} loading={loading} withPromoOrGiftCardRow withCashbackPointsRow />
                <ExpectedCashback />
                <CheckoutThreshold />
                <PayButton />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center p-md">
              <EmptyBasket />
            </div>
          )}
        </>
      }
    />
  );
};

type LeftColumnProps = PropsWithChildren<{ onReturn?: () => void }>;

OrderPaymentScreen.LeftColumn = ({ children, onReturn }: LeftColumnProps) => {
  const { navigate } = useRouter();
  const theme = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderPaymentScreen' });
  const _onReturn = onReturn
    ? onReturn
    : () => navigate({ web: { to: SCREENS.CHECKOUT }, mobile: { path: Routes.Checkout } });

  return (
    <div className="pb-lg">
      <div className="space-y-md">
        <button
          onClick={_onReturn}
          className="flex items-center w-full p-md text-sm text-muted-foreground lg:p-none lg:w-fit"
        >
          <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
          {t('buttons.return')}
        </button>
        {children}
      </div>
    </div>
  );
};

const Mobile = ({ className }: { className?: string }) => {
  const { items } = useBasket();
  const { data, loading } = useOrderFlow();
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderPaymentScreen' });
  const { formatCurrency } = useAppMethods();

  const [itemsExpanded, setItemsExpanded] = useState(false);

  const itemCount = items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <FixedPageLayout
      footer={
        items.length > 0 &&
        !isMobileApp && (
          <CheckoutButton className={classnames('bg-brand-card !pt-none')}>
            <PayButton showTerms className="mt-md text-base" />
          </CheckoutButton>
        )
      }
    >
      <div className={classnames('grid grid-cols-12', className)}>
        <div className="col-span-12 lg:col-span-8 xl:col-span-9">
          {!isMobileApp && (
            <button
              type="button"
              onClick={() => navigate({ web: { to: SCREENS.CHECKOUT } })}
              className={returnLinkStyle}
            >
              <ChevronLeft className="w-4 h-4" />
              {t('buttons.return')}
            </button>
          )}
          {Boolean(data.id) && <PaymentInfoSection show className={classnames({ 'mt-sm': isMobileApp })} />}
        </div>
        <div className="col-span-12 lg:col-span-4 xl:col-span-3">
          {items.length > 0 ? (
            <Section show title="" className="!p-md">
              <div className="rounded-xl border-xs border-brand-border bg-brand-card p-md">
                <button
                  type="button"
                  onClick={() => setItemsExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-xs">
                    <h3 className="text-base font-semibold text-brand-text-primary">{t('sections.order_summary')}</h3>
                    <span className="text-xs text-muted-foreground">
                      ({t('labels.item_count', { count: itemCount })})
                    </span>
                  </div>
                  <div className="flex items-center gap-xs">
                    <span className="text-sm font-semibold text-brand-text-primary tabular-nums">
                      {formatCurrency(data.total)}
                    </span>
                    <ChevronDown
                      className={classnames('w-4 h-4 text-muted-foreground transition-transform duration-200', {
                        'rotate-180': itemsExpanded,
                      })}
                    />
                  </div>
                </button>
                {itemsExpanded && (
                  <div className="mt-md space-y-sm border-t-xs border-brand-border pt-md">
                    <DeliveryThreshold />
                    {items.map((item, index) => (
                      <div key={`basket-item-${index}`} className="space-y-2xs">
                        <div className="text-sm flex justify-between gap-md">
                          <span className="text-muted-foreground">
                            {item.quantity}x {item.entry?.name}
                          </span>
                          <PriceComparison
                            className="shrink-0"
                            originalPrice={item.original_price}
                            actualPrice={item.actual_price}
                          />
                        </div>
                        <OfferLineBreakdown item={item} />
                        <OrderLineBadges badges={item.badges} />
                        <BasketItemModifiers item={item} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-md pt-md border-t-xs border-brand-border space-y-md">
                  <OrderPaymentBreakdown order={data} loading={loading} withPromoOrGiftCardRow withCashbackPointsRow />
                  <ExpectedCashback />
                  <CheckoutThreshold />
                </div>
              </div>
            </Section>
          ) : (
            <EmptyBasket />
          )}
        </div>
      </div>
    </FixedPageLayout>
  );
};

const returnLinkStyle =
  'flex items-center gap-2xs text-sm text-brand-text-primary hover:text-brand-primary transition-colors p-md lg:px-none lg:pt-lg lg:pb-md';

const EmptyBasket = (): ReactElement => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderPaymentScreen' });

  return (
    <div className="grow w-full flex flex-col justify-center items-center z-1">
      <EmptyBasketIllustration />
      <p className="text-center text-sm text-muted-foreground">{t('labels.add_items')}</p>
    </div>
  );
};

export default OrderPaymentScreen;
